import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const VALUES = [
  {
    title: 'Customer-centric',
    description: 'We build features that solve real problems for real businesses.',
  },
  {
    title: 'Innovation',
    description: "We push the boundaries of what's possible with automation.",
  },
  {
    title: 'Security first',
    description: 'Enterprise-grade security is built into everything we do.',
  },
  {
    title: 'Collaboration',
    description: 'Great products come from diverse teams working together.',
  },
];

const LEADERSHIP = [
  { name: 'Frank Burkitt', role: 'CEO & Founder' },
  { name: 'Gail McGiffin', role: 'Chief Solutions Officer' },
];

export function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About Us - CriticalBridge.AI</title>
        <meta
          name="description"
          content="CriticalBridge.AI's mission is to make agentic AI accessible, scalable, and focused on delivering measurable business value — in the industries where governance is mandatory."
        />
        <meta
          name="keywords"
          content="about criticalbridge, agentic AI company, P&C insurance AI, company mission"
        />
        <meta property="og:title" content="About Us - CriticalBridge.AI" />
        <meta
          property="og:description"
          content="Making agentic AI accountable for real business outcomes."
        />
        <link rel="canonical" href="https://www.criticalbridge.ai/about" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Company / About
          </div>
          <h1 className="mt-4 max-w-[24ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl">
            Making agentic AI accountable for real business outcomes.
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            We're on a mission to make agentic AI accessible, scalable, and focused on delivering
            measurable business value — in the industries where governance is mandatory.
          </p>
        </div>
      </section>

      {/* Our story */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Our story
          </span>
          <h2 className="mt-2 text-3xl font-bold">Born from a gap everyone could see.</h2>
          <p className="mt-4 max-w-[68ch] text-muted-foreground">
            Founded in 2025, CriticalBridge.AI began with a simple observation: despite
            near-universal AI adoption — 88% of organizations deploying AI in at least one function,
            enterprise GenAI spending surging past $37 billion — the overwhelming majority of
            enterprises are failing to translate investment into business value. Over 95% of GenAI
            pilots never deliver measurable P&amp;L impact, and the agents that do ship run
            ungoverned.
          </p>
          <p className="mt-4 max-w-[68ch] text-muted-foreground">
            Our founders had lived that gap at scale — advising the C-suite, leading national
            practices, and building enterprise technology. So we built the missing layer: a platform
            where business and domain experts, not just data scientists, can build, deploy, and
            govern AI agents and end-to-end workflows inside the enterprise security perimeter —
            starting with the industry where the stakes and the standards are highest, P&amp;C
            insurance.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="border-y bg-card py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Values
          </span>
          <h2 className="mt-2 text-3xl font-bold">What we hold ourselves to.</h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((value) => (
              <div key={value.title} className="rounded-lg border bg-background p-5">
                <h3 className="text-lg font-bold">{value.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Leadership
          </span>
          <h2 className="mt-2 text-3xl font-bold">Leadership team</h2>

          <div className="mt-8 grid max-w-xl gap-4 sm:grid-cols-2">
            {LEADERSHIP.map((person) => (
              <div key={person.name} className="rounded-lg border bg-card p-5">
                <h3 className="text-lg font-bold">{person.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{person.role}</p>
              </div>
            ))}
          </div>

          <Button variant="outline" className="mt-6" asChild>
            <Link to="/contact">Get in touch →</Link>
          </Button>
        </div>
      </section>
    </>
  );
}
